import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import AppDialog from '../../../../../../../components/common/AppDialog';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import {
  BranchData,
  ClientDataType,
  SubBranchData,
} from '../../../../../../../context/clients/ClientsContextProvider';
import { SVG } from '../../../../../../../constants/assetLists';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import BranchTreeAutocomplete from '../../../../../../../components/common/BranchTreeAutocomplete';
import {
  BranchNodeFormValues,
  FolderValues,
  isGeneralRegulationFolder,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  NetworkNodeFormValues,
  RegulationFormValues,
  SubBranchNodeFormValues,
  findFieldPathForNodeId,
} from '../../../types';
import { findParentBranchId } from '../../../../../../../utils';

type FolderOption = {
  label: string;
  value: string;
  icon: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  selectedFieldPath: NetworkNodeFieldPath;
  editingRegulationId?: string;
  selectedFolderId: string | null;
};

const mapSubBranchToTree = (
  sub: SubBranchNodeFormValues,
  branchId: string,
  clientId: string,
): SubBranchData => ({
  id: sub.id,
  name: sub.name,
  icon: '',
  isClient: false,
  isBranch: false,
  isSubBranch: true,
  clientId,
  branchId,
});

const mapBranchToTree = (
  branch: BranchNodeFormValues,
  clientId: string,
): BranchData => ({
  id: branch.id,
  name: branch.name,
  icon: '',
  isClient: false,
  isBranch: true,
  isSubBranch: false,
  clientId,
  subBranches: (branch.subBranches ?? []).map((sb) =>
    mapSubBranchToTree(sb, branch.id, clientId),
  ),
});

const mapNetworkNodeToClientTree = (
  client: NetworkNodeFormValues,
): ClientDataType => ({
  id: client.id,
  name: client.name,
  icon: '',
  isClient: true,
  isBranch: false,
  isSubBranch: false,
  branches: client.branches.map((b) => mapBranchToTree(b, client.id)),
});

const getCurrentNodeId = (
  network: NetworkNodeFormValues[],
  path: NetworkNodeFieldPath,
): string | null => {
  const parts = path.split('.');
  if (parts.length === 2) return network[parseInt(parts[1])]?.id ?? null;
  if (parts.length === 4)
    return (
      network[parseInt(parts[1])]?.branches[parseInt(parts[3])]?.id ?? null
    );
  if (parts.length === 6)
    return (
      network[parseInt(parts[1])]?.branches[parseInt(parts[3])]?.subBranches[
        parseInt(parts[5])
      ]?.id ?? null
    );
  return null;
};

const buildRegulationFolderOptions = (
  network: NetworkNodeFormValues[],
  nodeId: string | null,
): FolderOption[] => {
  if (!nodeId) return [];
  const targetPath = findFieldPathForNodeId(network, nodeId);
  if (!targetPath) return [];

  const parts = targetPath.split('.');
  let targetNode:
    | NetworkNodeFormValues
    | BranchNodeFormValues
    | SubBranchNodeFormValues
    | undefined;

  if (parts.length === 2) {
    targetNode = network[parseInt(parts[1])];
  } else if (parts.length === 4) {
    targetNode = network[parseInt(parts[1])]?.branches[parseInt(parts[3])];
  } else if (parts.length === 6) {
    targetNode =
      network[parseInt(parts[1])]?.branches[parseInt(parts[3])]?.subBranches[
        parseInt(parts[5])
      ];
  }

  if (!targetNode) return [];

  const folders: FolderValues[] = targetNode.regulationFolders ?? [];

  return folders
    .filter((f) => !isGeneralRegulationFolder(f))
    .map((f) => ({
      label: f.name,
      value: f.id,
      icon: SVG.folderBlackIcon,
    }));
};

function DuplicateRegulationDialog({
  open,
  onClose,
  onSaved,
  selectedFieldPath,
  editingRegulationId,
  selectedFolderId,
}: Props) {
  const { watch, getValues, setValue } =
    useFormContext<NetworkDetailsFormValues>();

  const network = watch('network') || [];

  const [titleValue, setTitleValue] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [selectedSubBranchId, setSelectedSubBranchId] = useState<string | null>(
    null,
  );
  const [selectedFolderOption, setSelectedFolderOption] =
    useState<FolderOption | null>(null);

  useEffect(() => {
    if (!open) {
      setTitleValue('');
      setSelectedBranchId(null);
      setSelectedSubBranchId(null);
      setSelectedFolderOption(null);
      return;
    }

    const regulations: RegulationFormValues[] =
      getValues(`${selectedFieldPath}.regulations`) ?? [];
    const source = regulations.find((r) => r.id === editingRegulationId);
    if (source) {
      setTitleValue(`${source.name} - עותק`);
    }
    const parts = selectedFieldPath.split('.');
    const clientIdx = parseInt(parts[1]);
    const currentClient = network[clientIdx];

    if (parts.length >= 4) {
      const branchIdx = parseInt(parts[3]);
      const branch = currentClient?.branches[branchIdx];
      setSelectedBranchId(branch?.id ?? null);

      if (parts.length === 6) {
        const subBranchIdx = parseInt(parts[5]);
        const subBranch = branch?.subBranches?.[subBranchIdx];
        setSelectedSubBranchId(subBranch?.id ?? null);

        if (selectedFolderId) {
          const effectiveId = subBranch?.id ?? branch?.id ?? null;
          const opts = buildRegulationFolderOptions(network, effectiveId);
          setSelectedFolderOption(
            opts.find((o) => o.value === selectedFolderId) ?? null,
          );
        }
      } else {
        setSelectedSubBranchId(null);
        if (selectedFolderId && branch?.id) {
          const opts = buildRegulationFolderOptions(network, branch.id);
          setSelectedFolderOption(
            opts.find((o) => o.value === selectedFolderId) ?? null,
          );
        }
      }
    } else {
      // At client level — fall back to the node ID for shift/folder init
      const currentId = getCurrentNodeId(network, selectedFieldPath);
      setSelectedBranchId(currentId);
      setSelectedSubBranchId(null);

      if (selectedFolderId && currentId) {
        const opts = buildRegulationFolderOptions(network, currentId);
        setSelectedFolderOption(
          opts.find((o) => o.value === selectedFolderId) ?? null,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filteredClients = useMemo((): ClientDataType[] => {
    const networkIndex = parseInt(selectedFieldPath.split('.')[1]);
    const client = network[networkIndex];
    return client ? [mapNetworkNodeToClientTree(client)] : [];
  }, [network, selectedFieldPath]);

  // ── effective target: subBranch if selected, else branch ──────────────────
  const effectiveNodeId =
    selectedSubBranchId ?? selectedBranchId ?? filteredClients[0]?.id ?? null;

  const targetOptions = useMemo(
    () => buildRegulationFolderOptions(network, effectiveNodeId),
    [network, effectiveNodeId],
  );

  useEffect(() => {
    if (
      selectedFolderOption &&
      !targetOptions.some((o) => o.value === selectedFolderOption.value)
    ) {
      setSelectedFolderOption(null);
    }
  }, [targetOptions, selectedFolderOption]);

  const handleBranchTreeChange = (
    nodes: (ClientDataType | BranchData | SubBranchData)[],
  ) => {
    const incomingBranchId = nodes.find((n) => n.isBranch)?.id ?? null;
    const incomingSubBranchId = nodes.find((n) => n.isSubBranch)?.id ?? null;

    let newBranchId = incomingBranchId;
    let newSubBranchId = incomingSubBranchId;

    // Branch was explicitly removed (was selected, now gone)
    // Clear subBranch too — must check this before the auto-add rule below
    if (selectedBranchId && !incomingBranchId) {
      newSubBranchId = null;
    }
    // SubBranch newly selected without its parent branch → auto-add parent
    else if (incomingSubBranchId && !incomingBranchId) {
      newBranchId = findParentBranchId(network, incomingSubBranchId);
    }
    // Branch swapped while a subBranch is selected → clear subBranch if it
    // doesn't belong to the new branch
    else if (incomingBranchId && incomingSubBranchId) {
      const parentBranchId = findParentBranchId(network, incomingSubBranchId);
      if (parentBranchId !== incomingBranchId) {
        newSubBranchId = null;
      }
    }

    setSelectedBranchId(newBranchId);
    setSelectedSubBranchId(newSubBranchId);
    setSelectedFolderOption(null);
  };

  const handleOnClose = () => {
    onClose();
  };

  const handleSave = () => {
    if (!titleValue || !effectiveNodeId || !selectedFolderOption) return;

    const allRegulations: RegulationFormValues[] =
      getValues(`${selectedFieldPath}.regulations`) ?? [];
    const source = allRegulations.find((r) => r.id === editingRegulationId);
    if (!source) return;

    const targetPath = findFieldPathForNodeId(network, effectiveNodeId);
    if (!targetPath) return;

    const targetRegulations: RegulationFormValues[] =
      getValues(`${targetPath}.regulations`) ?? [];

    const cloned: RegulationFormValues = {
      ...source,
      id: crypto.randomUUID(),
      name: titleValue,
      folderId: selectedFolderOption.value,
      department: '',
      assignee: '',
    };

    setValue(`${targetPath}.regulations`, [...targetRegulations, cloned], {
      shouldValidate: true,
      shouldDirty: true,
    });

    onSaved?.();
    handleOnClose();
  };

  // ids that represent real branch/subBranch nodes — excludes the client's
  // own id (used internally as a fallback when duplicating at the client
  // level) so it never leaks into BranchTreeAutocomplete's value and shows
  // up as a spurious, non-selectable chip there
  const validTreeIds = useMemo(() => {
    const ids = new Set<string>();
    filteredClients.forEach((client) => {
      client.branches.forEach((branch) => {
        ids.add(branch.id);
        branch.subBranches.forEach((sb) => ids.add(sb.id));
      });
    });
    return ids;
  }, [filteredClients]);

  const treeValue = [selectedBranchId, selectedSubBranchId].filter(
    (id): id is string => Boolean(id) && validTreeIds.has(id as string),
  );

  const isSaveDisabled =
    !titleValue.trim() || !effectiveNodeId || !selectedFolderOption;

  return (
    <AppDialog
      showModel={open}
      onClose={handleOnClose}
      showCloseIcon={false}
      containerClasses="!rounded-[12px] !pt-3 !border-none !overflow-visible"
      headerClasses="!px-0"
      width={480}
      titleField={<p className="m-0 text-base font-medium">שכפול רגולציה</p>}
      showHeaderLine={false}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <AppTextInput
            className="border-0"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
          />

          <BranchTreeAutocomplete
            disablePortal={false}
            required
            label="סניף"
            showClientChip
            showSelectedTags
            allClients={filteredClients}
            value={treeValue}
            onChange={handleBranchTreeChange}
          />

          <AppMUIAutocomplete
            disablePortal={false}
            label="תיקייה"
            size="small"
            options={targetOptions}
            getOptionLabel={(o) => o.label}
            fullWidth
            required
            value={selectedFolderOption}
            onChange={(_e, val) =>
              setSelectedFolderOption(val as FolderOption | null)
            }
            isOptionEqualToValue={(opt, val) =>
              (opt as FolderOption).value === (val as FolderOption).value
            }
            renderValue={(value) =>
              value && (
                <div className="flex items-center gap-2">
                  <img
                    src={(value as FolderOption)?.icon}
                    alt="icon"
                    className="w-[20px] h-[20px] object-contain"
                  />
                  <span className="text-base text-textColor">
                    {(value as FolderOption)?.label}
                  </span>
                </div>
              )
            }
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              const opt = option as FolderOption;
              return (
                <li key={key} {...optionProps}>
                  <img
                    src={opt?.icon}
                    alt="icon"
                    className="w-[20px] h-[20px] object-contain"
                  />
                  <span className="ms-2 text-base text-textColor">
                    {opt?.label}
                  </span>
                </li>
              );
            }}
          />
        </div>

        <div className="flex justify-end">
          <div className="flex items-center gap-5">
            <button
              type="button"
              className="text-base font-medium text-darkGray"
              onClick={handleOnClose}
            >
              ביטול
            </button>
            <button
              type="button"
              disabled={isSaveDisabled}
              className={`text-base font-medium ${isSaveDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-darkGreen'}`}
              onClick={handleSave}
            >
              שמירה
            </button>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default DuplicateRegulationDialog;
